// Copied from API/utils/vat.js. Keep the two in sync — both test suites run the
// same case table (utils/vat.cases.js) precisely so a divergence fails loudly.

const VAT_MODES = ['none', 'include', 'exclude'];

// Round to 2dp, half-up on the absolute value so -1.005 -> -1.01 rather than -1.00.
//
// Do NOT reach for `Math.round(abs * 100) / 100`, with or without an EPSILON
// nudge. Multiplying by 100 loses the .5: 2.135 * 100 is 213.49999999999997,
// which rounds DOWN to 2.13. An `+ Number.EPSILON` nudge fixes 2.675 and looks
// convincing, but it still fails 1132 of the 20000 x.xx5 values below 200 —
// 2.135, 2.175, 2.385, 4.015 and so on. This was measured, not guessed.
//
// The exponent shift below is exact on all of them, because JS prints the
// shortest round-trip decimal: String(2.135) is "2.135", so "2.135e2" parses
// as exactly 213.5 and rounds up as it should.
function round2(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);

  // Both conversions need plain decimal strings. JS switches to exponential
  // notation outside roughly 1e-7..1e21, where "1e-9e2" would parse as NaN.
  // Money never goes there; fall back rather than return NaN if it does.
  if (abs !== 0 && (abs < 1e-6 || abs >= 1e15)) {
    return sign * Math.round(abs * 100) / 100;
  }

  const shifted = Math.round(Number(`${abs}e2`));
  return sign * Number(`${shifted}e-2`);
}

// Which rate actually applies to a line: the item's own override when it has
// one, otherwise the branch default. A non-VAT line is always 0.
// An explicit item rate of 0 is a real answer and beats the branch rate.
function resolveVatRate({ vatMode, itemRate, branchRate }) {
  if (vatMode !== 'include' && vatMode !== 'exclude') return 0;
  const useItem = itemRate !== null && itemRate !== undefined && itemRate !== '';
  const n = Number(useItem ? itemRate : branchRate);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Invoice items are snake_case everywhere they are stored, posted and rendered
// (unit_price, discount_amount), while this module was written in camelCase.
// Accept both rather than quietly returning a zero-value line for a real item.
// `quantity` is spelt the same in both conventions, so it needs no alias.
function resolveVatMode(item) {
  const mode = item.vatMode ?? item.vat_mode;
  return VAT_MODES.includes(mode) ? mode : 'none';
}

// gross = the discounted line amount as entered, before VAT interpretation.
//   none    -> net = gross,                vat = 0
//   exclude -> net = gross,                vat = net * rate/100,  total = net + vat
//   include -> total = gross,              net = total / (1 + rate/100),
//              vat = total - net  (by subtraction, so net + vat === total exactly)
function calculateLine(item = {}) {
  // A missing price must be loud. Returning zeros for it is how a whole invoice
  // silently prices at nothing. An explicit 0 is a real price and still works.
  const rawPrice = item.unitPrice ?? item.unit_price;
  if (rawPrice === undefined) {
    throw new Error(
      'calculateLine: unitPrice (or unit_price) is required; pass 0 for a free line'
    );
  }

  const qty = Number(item.quantity) || 0;
  const price = Number(rawPrice) || 0;
  const discount = Number(item.discountAmount ?? item.discount_amount ?? 0) || 0;
  const mode = resolveVatMode(item);
  const rate = mode === 'none' ? 0 : (Number(item.vatRate ?? item.vat_rate ?? 0) || 0);

  const gross = Math.max(0, round2(round2(qty * price) - discount));

  if (rate > 0 && mode === 'exclude') {
    const vat = round2(gross * rate / 100);
    return { gross, net: gross, vat, total: round2(gross + vat) };
  }

  if (rate > 0 && mode === 'include') {
    const net = round2(gross / (1 + rate / 100));
    return { gross, net, vat: round2(gross - net), total: gross };
  }

  return { gross, net: gross, vat: 0, total: gross };
}

// Roll lines into invoice totals.
//   subtotal  = sum of line net
//   itemsVat  = sum of line vat
//   legacyTax = the existing invoice-level tax_rate, applied ONLY to the net of
//               lines whose mode is 'none'. This is what keeps treatments working
//               as they always have while stopping a VAT-liable line being taxed
//               twice. A line that opted into VAT and resolved to 0% is still an
//               opt-in, so it stays out of this base.
//   amount    = subtotal - invoice discount + taxAmount
//
// The invoice-level discount is spread across the legacy base PRO-RATA by that
// base's share of the subtotal, because today's invoice-api.js computes
// `taxAmount = (subtotal - discount) * rate/100` — it taxes the DISCOUNTED
// total. On a legacy invoice every line is 'none', so legacyBase === subtotal,
// the share is 1, and legacyTaxBase collapses to exactly `subtotal - discount`:
// the arithmetic is then identical to today's, to the satang. On a mixed
// invoice the VAT-liable lines take their own proportion of the discount out of
// their own (already separately taxed) net, so nothing is taxed twice and
// nothing escapes the discount. The base floors at 0 so a discount larger than
// the subtotal cannot produce negative tax.
function calculateInvoiceTotals({ items = [], invoiceDiscount = 0, invoiceTaxRate = 0 }) {
  let subtotal = 0;
  let itemsVat = 0;
  let legacyBase = 0;

  for (const item of items) {
    const line = calculateLine(item);
    subtotal = round2(subtotal + line.net);
    itemsVat = round2(itemsVat + line.vat);
    if (resolveVatMode(item) === 'none') legacyBase = round2(legacyBase + line.net);
  }

  const rate = Number(invoiceTaxRate) || 0;
  const discount = Number(invoiceDiscount) || 0;
  const legacyShare = subtotal > 0 ? legacyBase / subtotal : 0;
  const legacyTaxBase = Math.max(0, legacyBase - discount * legacyShare);
  const legacyTax = round2(legacyTaxBase * rate / 100);
  const taxAmount = round2(itemsVat + legacyTax);
  const amount = round2(subtotal - discount + taxAmount);

  return { subtotal, itemsVat, legacyTax, taxAmount, discount, amount };
}

export { VAT_MODES, round2, resolveVatRate, calculateLine, calculateInvoiceTotals };
